import { createSlice } from "@reduxjs/toolkit";
import { qualityInspection, userClockIn, userClockCheckIns, getActiveReps, getActiveRecruitees, getTrainingPercentage, updateActiveReps, forgotPassword, getLedgerHistory, fetchRecipients, sendPoints, requestPoints, challengePoints, challengeGroup, getOpenChallenges, payChallenge, cancelChallenge, nudgeChallenge, acceptChallenge, wonChallenge, lostChallenge, getOpenRequests, payRequest, cancelRequest, nudgeRequest, getPointsToPayEligibility, getPointsToPayPending, convertPointsToPay, cancelPointsToPay, createPointChallenge, createPointChallengeGroup, listPointChallenges, submitPointChallenge, decidePointChallenge, cancelPointChallenge, retractPointChallenge, nudgePointChallenge } from "./userActions";

const initialState = {
  qualityInspectionLoading: false,
  userClockInLoading: false,
  userClockCheckInsLoading: false,
  userActiveRepsLoading: false,
  userActiveRecruiteesLoading: false,
  userTrainingPercentageLoading: false,
  userForgotPasswordLoading: false,
  userLedgerHistoryLoading: false,
  fetchRecipientsLoading: false,
  sendPointsLoading: false,
  requestPointsLoading: false,
  challengePointsLoading: false,
  openChallengesLoading: false,
  challengeActionLoading: false,
  openRequestsLoading: false,
  requestActionLoading: false,
  pointsToPayEligibilityLoading: false,
  pointsToPayPendingLoading: false,
  pointsToPayActionLoading: false,
  // Point Challenges (bounty tab): one create flag, one list flag, and one
  // shared per-row action flag (submit/decide/cancel/nudge are mutually
  // exclusive in the UI — buttons disable themselves on pending).
  createPointChallengeLoading: false,
  pointChallengesLoading: false,
  pointChallengeActionLoading: false,
  userClockInResponseData: [],
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setActiveRepsData: (state, { payload }) => {
      state.ActiveRepsData = payload;
    },
    setActiveRecruiteesData: (state, { payload }) => {
      state.ActiveRecruiteesData = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(qualityInspection.pending, (state) => {
        state.qualityInspectionLoading = true;
      })
      .addCase(qualityInspection.fulfilled, (state) => {
        state.qualityInspectionLoading = false;
      })
      .addCase(qualityInspection.rejected, (state) => {
        state.qualityInspectionLoading = false;
      })
      .addCase(userClockIn.pending, (state) => {
        state.userClockInLoading = true;
      })
      .addCase(userClockIn.fulfilled, (state, action) => {
        state.userClockInResponseData = action.payload;
        state.userClockInLoading = false;
      })
      .addCase(userClockIn.rejected, (state) => {
        state.userClockInLoading = false;
      })
      .addCase(userClockCheckIns.pending, (state) => {
        state.userClockCheckInsLoading = true;
      })
      .addCase(userClockCheckIns.fulfilled, (state, action) => {
        state.userClockCheckInsData = action.payload;
        state.userClockCheckInsLoading = false;
      })
      .addCase(userClockCheckIns.rejected, (state) => {
        state.userClockCheckInsLoading = false;
      })
      .addCase(getActiveReps.pending, (state) => {
        state.userActiveRepsLoading = true;
      })
      .addCase(getActiveReps.fulfilled, (state) => {
        state.userActiveRepsLoading = false;
      })
      .addCase(getActiveReps.rejected, (state) => {
        state.userActiveRepsLoading = false;
      })
      .addCase(getActiveRecruitees.pending, (state) => {
        state.userActiveRecruiteesLoading = true;
      })
      .addCase(getActiveRecruitees.fulfilled, (state) => {
        state.userActiveRecruiteesLoading = false;
      })
      .addCase(getActiveRecruitees.rejected, (state) => {
        state.userActiveRecruiteesLoading = false;
      })
      .addCase(getTrainingPercentage.pending, (state) => {
        state.userTrainingPercentageLoading = true;
      })
      .addCase(getTrainingPercentage.fulfilled, (state) => {
        state.userTrainingPercentageLoading = false;
      })
      .addCase(getTrainingPercentage.rejected, (state) => {
        state.userTrainingPercentageLoading = false;
      })
      .addCase(updateActiveReps.pending, (state) => {
        state.userActiveRepsLoading = true;
      })
      .addCase(updateActiveReps.fulfilled, (state) => {
        state.userActiveRepsLoading = false;
      })
      .addCase(updateActiveReps.rejected, (state) => {
        state.userActiveRepsLoading = false;
      })
      .addCase(forgotPassword.pending, (state) => {
        state.userForgotPasswordLoading = true;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.userForgotPasswordLoading = false;
      })
      .addCase(forgotPassword.rejected, (state) => {
        state.userForgotPasswordLoading = false;
      })
      .addCase(getLedgerHistory.pending, (state) => {
        state.userLedgerHistoryLoading = true;
      })
      .addCase(getLedgerHistory.fulfilled, (state) => {
        state.userLedgerHistoryLoading = false;
      })
      .addCase(getLedgerHistory.rejected, (state) => {
        state.userLedgerHistoryLoading = false;
      })
      .addCase(fetchRecipients.pending, (state) => {
        state.fetchRecipientsLoading = true;
      })
      .addCase(fetchRecipients.fulfilled, (state) => {
        state.fetchRecipientsLoading = false;
      })
      .addCase(fetchRecipients.rejected, (state) => {
        state.fetchRecipientsLoading = false;
      })
      .addCase(sendPoints.pending, (state) => {
        state.sendPointsLoading = true;
      })
      .addCase(sendPoints.fulfilled, (state) => {
        state.sendPointsLoading = false;
      })
      .addCase(sendPoints.rejected, (state) => {
        state.sendPointsLoading = false;
      })
      .addCase(requestPoints.pending, (state) => {
        state.requestPointsLoading = true;
      })
      .addCase(requestPoints.fulfilled, (state) => {
        state.requestPointsLoading = false;
      })
      .addCase(requestPoints.rejected, (state) => {
        state.requestPointsLoading = false;
      })
      .addCase(challengePoints.pending, (state) => {
        state.challengePointsLoading = true;
      })
      .addCase(challengePoints.fulfilled, (state) => {
        state.challengePointsLoading = false;
      })
      .addCase(challengePoints.rejected, (state) => {
        state.challengePointsLoading = false;
      })
      // Multi-send create shares the existing challenge-create spinner.
      .addCase(challengeGroup.pending, (state) => {
        state.challengePointsLoading = true;
      })
      .addCase(challengeGroup.fulfilled, (state) => {
        state.challengePointsLoading = false;
      })
      .addCase(challengeGroup.rejected, (state) => {
        state.challengePointsLoading = false;
      })
      .addCase(getOpenChallenges.pending, (state) => {
        state.openChallengesLoading = true;
      })
      .addCase(getOpenChallenges.fulfilled, (state) => {
        state.openChallengesLoading = false;
      })
      .addCase(getOpenChallenges.rejected, (state) => {
        state.openChallengesLoading = false;
      })
      // payChallenge / cancelChallenge / nudgeChallenge / acceptChallenge
      // share one challengeActionLoading flag — only one per-row mutation
      // can be in flight at a time (buttons disable themselves on pending).
      .addCase(payChallenge.pending, (state) => {
        state.challengeActionLoading = true;
      })
      .addCase(payChallenge.fulfilled, (state) => {
        state.challengeActionLoading = false;
      })
      .addCase(payChallenge.rejected, (state) => {
        state.challengeActionLoading = false;
      })
      .addCase(cancelChallenge.pending, (state) => {
        state.challengeActionLoading = true;
      })
      .addCase(cancelChallenge.fulfilled, (state) => {
        state.challengeActionLoading = false;
      })
      .addCase(cancelChallenge.rejected, (state) => {
        state.challengeActionLoading = false;
      })
      .addCase(nudgeChallenge.pending, (state) => {
        state.challengeActionLoading = true;
      })
      .addCase(nudgeChallenge.fulfilled, (state) => {
        state.challengeActionLoading = false;
      })
      .addCase(nudgeChallenge.rejected, (state) => {
        state.challengeActionLoading = false;
      })
      .addCase(acceptChallenge.pending, (state) => {
        state.challengeActionLoading = true;
      })
      .addCase(acceptChallenge.fulfilled, (state) => {
        state.challengeActionLoading = false;
      })
      .addCase(acceptChallenge.rejected, (state) => {
        state.challengeActionLoading = false;
      })
      .addCase(wonChallenge.pending, (state) => {
        state.challengeActionLoading = true;
      })
      .addCase(wonChallenge.fulfilled, (state) => {
        state.challengeActionLoading = false;
      })
      .addCase(wonChallenge.rejected, (state) => {
        state.challengeActionLoading = false;
      })
      .addCase(lostChallenge.pending, (state) => {
        state.challengeActionLoading = true;
      })
      .addCase(lostChallenge.fulfilled, (state) => {
        state.challengeActionLoading = false;
      })
      .addCase(lostChallenge.rejected, (state) => {
        state.challengeActionLoading = false;
      })
      .addCase(getOpenRequests.pending, (state) => {
        state.openRequestsLoading = true;
      })
      .addCase(getOpenRequests.fulfilled, (state) => {
        state.openRequestsLoading = false;
      })
      .addCase(getOpenRequests.rejected, (state) => {
        state.openRequestsLoading = false;
      })
      // payRequest / cancelRequest / nudgeRequest share one
      // requestActionLoading flag — same one-mutation-at-a-time pattern as
      // the challenge actions above.
      .addCase(payRequest.pending, (state) => {
        state.requestActionLoading = true;
      })
      .addCase(payRequest.fulfilled, (state) => {
        state.requestActionLoading = false;
      })
      .addCase(payRequest.rejected, (state) => {
        state.requestActionLoading = false;
      })
      .addCase(cancelRequest.pending, (state) => {
        state.requestActionLoading = true;
      })
      .addCase(cancelRequest.fulfilled, (state) => {
        state.requestActionLoading = false;
      })
      .addCase(cancelRequest.rejected, (state) => {
        state.requestActionLoading = false;
      })
      .addCase(nudgeRequest.pending, (state) => {
        state.requestActionLoading = true;
      })
      .addCase(nudgeRequest.fulfilled, (state) => {
        state.requestActionLoading = false;
      })
      .addCase(nudgeRequest.rejected, (state) => {
        state.requestActionLoading = false;
      })
      // Points-to-Pay: convert + cancel share one pointsToPayActionLoading
      // flag (only one can be in flight at a time and they're mutually
      // exclusive in the dialog UI). Eligibility + pending are independent
      // reads with their own flags so the dialog can show a spinner per row.
      .addCase(getPointsToPayEligibility.pending, (state) => {
        state.pointsToPayEligibilityLoading = true;
      })
      .addCase(getPointsToPayEligibility.fulfilled, (state) => {
        state.pointsToPayEligibilityLoading = false;
      })
      .addCase(getPointsToPayEligibility.rejected, (state) => {
        state.pointsToPayEligibilityLoading = false;
      })
      .addCase(getPointsToPayPending.pending, (state) => {
        state.pointsToPayPendingLoading = true;
      })
      .addCase(getPointsToPayPending.fulfilled, (state) => {
        state.pointsToPayPendingLoading = false;
      })
      .addCase(getPointsToPayPending.rejected, (state) => {
        state.pointsToPayPendingLoading = false;
      })
      .addCase(convertPointsToPay.pending, (state) => {
        state.pointsToPayActionLoading = true;
      })
      .addCase(convertPointsToPay.fulfilled, (state) => {
        state.pointsToPayActionLoading = false;
      })
      .addCase(convertPointsToPay.rejected, (state) => {
        state.pointsToPayActionLoading = false;
      })
      .addCase(cancelPointsToPay.pending, (state) => {
        state.pointsToPayActionLoading = true;
      })
      .addCase(cancelPointsToPay.fulfilled, (state) => {
        state.pointsToPayActionLoading = false;
      })
      .addCase(cancelPointsToPay.rejected, (state) => {
        state.pointsToPayActionLoading = false;
      })
      // Point Challenges: create has its own spinner; list has its own; the
      // four per-row mutations (submit/decide/cancel/nudge) share one flag.
      .addCase(createPointChallenge.pending, (state) => {
        state.createPointChallengeLoading = true;
      })
      .addCase(createPointChallenge.fulfilled, (state) => {
        state.createPointChallengeLoading = false;
      })
      .addCase(createPointChallenge.rejected, (state) => {
        state.createPointChallengeLoading = false;
      })
      // Group create reuses the single-create spinner (mirrors how challengeGroup
      // reuses the BET create flag).
      .addCase(createPointChallengeGroup.pending, (state) => {
        state.createPointChallengeLoading = true;
      })
      .addCase(createPointChallengeGroup.fulfilled, (state) => {
        state.createPointChallengeLoading = false;
      })
      .addCase(createPointChallengeGroup.rejected, (state) => {
        state.createPointChallengeLoading = false;
      })
      .addCase(listPointChallenges.pending, (state) => {
        state.pointChallengesLoading = true;
      })
      .addCase(listPointChallenges.fulfilled, (state) => {
        state.pointChallengesLoading = false;
      })
      .addCase(listPointChallenges.rejected, (state) => {
        state.pointChallengesLoading = false;
      })
      .addCase(submitPointChallenge.pending, (state) => {
        state.pointChallengeActionLoading = true;
      })
      .addCase(submitPointChallenge.fulfilled, (state) => {
        state.pointChallengeActionLoading = false;
      })
      .addCase(submitPointChallenge.rejected, (state) => {
        state.pointChallengeActionLoading = false;
      })
      .addCase(decidePointChallenge.pending, (state) => {
        state.pointChallengeActionLoading = true;
      })
      .addCase(decidePointChallenge.fulfilled, (state) => {
        state.pointChallengeActionLoading = false;
      })
      .addCase(decidePointChallenge.rejected, (state) => {
        state.pointChallengeActionLoading = false;
      })
      .addCase(cancelPointChallenge.pending, (state) => {
        state.pointChallengeActionLoading = true;
      })
      .addCase(cancelPointChallenge.fulfilled, (state) => {
        state.pointChallengeActionLoading = false;
      })
      .addCase(cancelPointChallenge.rejected, (state) => {
        state.pointChallengeActionLoading = false;
      })
      .addCase(retractPointChallenge.pending, (state) => {
        state.pointChallengeActionLoading = true;
      })
      .addCase(retractPointChallenge.fulfilled, (state) => {
        state.pointChallengeActionLoading = false;
      })
      .addCase(retractPointChallenge.rejected, (state) => {
        state.pointChallengeActionLoading = false;
      })
      .addCase(nudgePointChallenge.pending, (state) => {
        state.pointChallengeActionLoading = true;
      })
      .addCase(nudgePointChallenge.fulfilled, (state) => {
        state.pointChallengeActionLoading = false;
      })
      .addCase(nudgePointChallenge.rejected, (state) => {
        state.pointChallengeActionLoading = false;
      });
  },
});

export const { setActiveRepsData, setActiveRecruiteesData } = userSlice.actions;
export default userSlice.reducer;
